import { IconType, StatusVariant } from '@factorialco/f0-react'
import {
  Desktop,
  HardDrive,
  Headset,
  Keyboard,
  Laptop,
  Mobile,
  Mouse,
  Settings,
} from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

export const ASSET_STATUS = {
  IN_STOCK: 'in_stock',
  ASSIGNED: 'assigned',
  MAINTENANCE: 'maintenance',
  RETIRED: 'retired',
} as const

const ASSET_STATUSES = [
  ASSET_STATUS.IN_STOCK,
  ASSET_STATUS.ASSIGNED,
  ASSET_STATUS.MAINTENANCE,
  ASSET_STATUS.RETIRED,
] as const

export type AssetStatus = (typeof ASSET_STATUSES)[number]

const ASSET_TYPES = [
  'laptop',
  'phone',
  'accessory',
  'screen',
  'mouse',
  'keyboard',
  'headset',
  'other',
] as const

export type AssetType = (typeof ASSET_TYPES)[number]

const ASSET_GROUPS = [...ASSET_STATUSES, ...ASSET_TYPES] as const

export type AssetGroup = (typeof ASSET_GROUPS)[number]

const ASSET_STATUS_VARIANTS: Record<AssetStatus, StatusVariant> = {
  in_stock: 'positive',
  assigned: 'info',
  maintenance: 'warning',
  retired: 'neutral',
} as const

const DELETED_STATUS_VARIANT: StatusVariant = 'critical' as const

const ASSET_TYPE_ICONS: Record<AssetType, IconType> = {
  laptop: Laptop,
  phone: Mobile,
  accessory: HardDrive,
  screen: Desktop,
  mouse: Mouse,
  keyboard: Keyboard,
  headset: Headset,
  other: Settings,
} as const

export const isValidAssetStatus = (status: string): status is AssetStatus => {
  return ASSET_STATUSES.map((statusItem) => statusItem.toString()).includes(status)
}

export const isValidAssetType = (type: string): type is AssetType => {
  return ASSET_TYPES.map((typeItem) => typeItem.toString()).includes(type)
}

export const isValidAssetGroup = (group: string): group is AssetGroup => {
  return isValidAssetStatus(group) || isValidAssetType(group)
}

const assetTranslateStatus = (status: AssetStatus): string => {
  switch (status) {
    case 'in_stock':
      return i18n.t('it_management.assets.status.in_stock')
    case 'assigned':
      return i18n.t('it_management.assets.status.assigned')
    case 'maintenance':
      return i18n.t('it_management.assets.status.maintenance')
    case 'retired':
      return i18n.t('it_management.assets.status.retired')
    default:
      return status
  }
}

export const getDeletedStatusTranslation = (): string => {
  return i18n.t('it_management.assets.status.deleted')
}

const assetStatusAsVariant = (status: AssetStatus): StatusVariant => {
  if (isValidAssetStatus(status)) {
    return ASSET_STATUS_VARIANTS[status]
  }
  return 'neutral'
}

const assetTypeAsIcon = (type: AssetType): IconType => {
  if (isValidAssetType(type)) {
    return ASSET_TYPE_ICONS[type]
  }
  return Settings
}

const assetTranslateType = (type: AssetType): string => {
  switch (type) {
    case 'laptop':
      return i18n.t('it_management.assets.type.laptop')
    case 'phone':
      return i18n.t('it_management.assets.type.phone')
    case 'accessory':
      return i18n.t('it_management.assets.type.accessory')
    case 'screen':
      return i18n.t('it_management.assets.type.screen')
    case 'mouse':
      return i18n.t('it_management.assets.type.mouse')
    case 'keyboard':
      return i18n.t('it_management.assets.type.keyboard')
    case 'headset':
      return i18n.t('it_management.assets.type.headset')
    case 'other':
      return i18n.t('it_management.assets.type.other')
    default:
      return type
  }
}

// Safe wrapper functions for use with GraphQL string data
export const safeAssetTranslateStatus = (status: string): string => {
  if (isValidAssetStatus(status)) {
    return assetTranslateStatus(status)
  }
  return status // Return original string if not a valid status
}

export const safeAssetStatusAsVariant = (status: string): StatusVariant => {
  if (isValidAssetStatus(status)) {
    return assetStatusAsVariant(status)
  }
  return 'neutral' // Default fallback
}

export const getDeletedStatusVariant = (): StatusVariant => {
  return DELETED_STATUS_VARIANT
}

export const safeAssetTypeAsIcon = (type: string): IconType => {
  if (isValidAssetType(type)) {
    return assetTypeAsIcon(type)
  }
  return Settings // Default fallback
}

export const safeAssetTranslateType = (type: string): string => {
  if (isValidAssetType(type)) {
    return assetTranslateType(type)
  }
  return type // Return original string if not a valid type
}
